/**
 * @fileoverview Assignment Manager Hook
 * 
 * Provides core assignment management functionality including creating,
 * updating, and deleting assignments with batch operations support.
 * Enhanced with ElevenLabs sync integration.
 * 
 * Requirements: 4.3, 4.4, 4.5, 4.7, 7.1, 7.2, 7.3, 7.4
 */

import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { elevenLabsSyncService, SyncOperationType } from '../services/ElevenLabsSyncService.js';

/**
 * Hook for managing knowledge base assignments
 * 
 * @param {KnowledgeBaseAssignmentConfig} config - Module configuration
 * @param {ModuleContext} moduleContext - Module context
 * @returns {Object} Assignment management utilities
 */
export function useAssignmentManager(config, moduleContext) {
  const [operationState, setOperationState] = useState({
    activeOperations: new Map(),
    batchOperations: new Map()
  });

  // Toggle single assignment with ElevenLabs sync
  const toggleAssignment = useCallback(async (fileId, parrotId, currentlyAssigned) => {
    const operationId = uuidv4();
    
    try {
      setOperationState(prev => ({
        ...prev,
        activeOperations: new Map(prev.activeOperations).set(operationId, {
          type: 'toggle',
          fileId,
          parrotId,
          status: 'processing'
        })
      }));

      // Initialize sync service if not already done
      if (!elevenLabsSyncService.apiClient) {
        elevenLabsSyncService.initialize(moduleContext.apiClient, config);
      }

      // Use ElevenLabs sync endpoints for assignment changes
      const syncOperation = currentlyAssigned ? SyncOperationType.UNASSIGN : SyncOperationType.ASSIGN;
      const syncResult = await elevenLabsSyncService.triggerSync(fileId, parrotId, syncOperation);

      setOperationState(prev => ({
        ...prev,
        activeOperations: new Map(prev.activeOperations).set(operationId, {
          type: 'toggle',
          fileId,
          parrotId,
          status: syncResult.success ? 'completed' : 'failed',
          result: syncResult,
          syncId: syncResult.syncId
        })
      }));

      if (!syncResult.success) {
        throw new Error(syncResult.error || 'Assignment sync failed');
      }

      return syncResult;

    } catch (error) {
      setOperationState(prev => ({
        ...prev,
        activeOperations: new Map(prev.activeOperations).set(operationId, {
          type: 'toggle',
          fileId,
          parrotId,
          status: 'failed',
          error: error.message
        })
      }));

      throw error;
    }
  }, [config, moduleContext.apiClient]);

  // Create batch assignment operation
  const createBatchOperation = useCallback(async (operation, fileIds, parrotIds) => {
    const batchId = uuidv4();
    const totalOperations = fileIds.length * parrotIds.length;
    
    const batchOp = {
      id: batchId,
      operation,
      fileIds,
      parrotIds,
      status: 'pending',
      progress: 0,
      totalOperations,
      completedOperations: 0,
      failedOperations: 0,
      conflicts: [],
      results: [],
      createdAt: new Date(),
      errors: []
    };

    setOperationState(prev => ({
      ...prev,
      batchOperations: new Map(prev.batchOperations).set(batchId, batchOp)
    }));

    return batchOp;
  }, []);

  // Execute batch assignment operation
  const executeBatchOperation = useCallback(async (batchId) => {
    const batchOp = operationState.batchOperations.get(batchId);
    if (!batchOp) {
      throw new Error(`Batch operation ${batchId} not found`);
    }

    try {
      // Update status to processing
      setOperationState(prev => ({
        ...prev,
        batchOperations: new Map(prev.batchOperations).set(batchId, {
          ...batchOp,
          status: 'processing',
          startedAt: new Date()
        })
      }));

      const results = [];
      const conflicts = [];
      let completedOperations = 0;
      let failedOperations = 0;

      // Process each file-parrot combination
      for (const fileId of batchOp.fileIds) {
        for (const parrotId of batchOp.parrotIds) {
          try {
            let result;
            
            // Initialize sync service if not already done
            if (!elevenLabsSyncService.apiClient) {
              elevenLabsSyncService.initialize(moduleContext.apiClient, config);
            }

            switch (batchOp.operation) {
              case 'assign':
                result = await elevenLabsSyncService.triggerSync(fileId, parrotId, SyncOperationType.ASSIGN);
                break;
              
              case 'unassign':
                result = await elevenLabsSyncService.triggerSync(fileId, parrotId, SyncOperationType.UNASSIGN);
                break;
              
              case 'reassign':
                // First unassign, then assign
                await elevenLabsSyncService.triggerSync(fileId, parrotId, SyncOperationType.UNASSIGN);
                result = await elevenLabsSyncService.triggerSync(fileId, parrotId, SyncOperationType.ASSIGN);
                break;
              
              default:
                throw new Error(`Unknown operation: ${batchOp.operation}`);
            }

            results.push({
              fileId,
              parrotId,
              status: 'success',
              result
            });
            
            completedOperations++;

          } catch (error) {
            // Check if this is a conflict that can be resolved
            if (error.status === 409 || error.message.includes('conflict')) {
              conflicts.push({
                id: uuidv4(),
                type: 'duplicate_assignment',
                fileId,
                parrotId,
                message: error.message,
                details: error.details || {},
                resolutionStrategy: 'user_choice',
                resolutionOptions: ['skip', 'overwrite', 'merge']
              });
            } else {
              results.push({
                fileId,
                parrotId,
                status: 'failed',
                error: error.message
              });
              
              failedOperations++;
            }
          }

          // Update progress
          const progress = Math.round(((completedOperations + failedOperations + conflicts.length) / batchOp.totalOperations) * 100);
          
          setOperationState(prev => ({
            ...prev,
            batchOperations: new Map(prev.batchOperations).set(batchId, {
              ...prev.batchOperations.get(batchId),
              progress,
              completedOperations,
              failedOperations,
              conflicts,
              results
            })
          }));
        }
      }

      // Final update
      const finalStatus = conflicts.length > 0 ? 'conflicts' : 
                         failedOperations > 0 ? 'partial_failure' : 'completed';

      setOperationState(prev => ({
        ...prev,
        batchOperations: new Map(prev.batchOperations).set(batchId, {
          ...prev.batchOperations.get(batchId),
          status: finalStatus,
          completedAt: new Date(),
          progress: 100
        })
      }));

      return {
        status: finalStatus,
        results,
        conflicts,
        completedOperations,
        failedOperations,
        totalOperations: batchOp.totalOperations
      };

    } catch (error) {
      setOperationState(prev => ({
        ...prev,
        batchOperations: new Map(prev.batchOperations).set(batchId, {
          ...prev.batchOperations.get(batchId),
          status: 'failed',
          error: error.message,
          completedAt: new Date()
        })
      }));

      throw error;
    }
  }, [operationState.batchOperations, config.context, moduleContext.apiClient]);

  // Check for assignment conflicts
  const checkConflicts = useCallback(async (operations) => {
    try {
      const response = await moduleContext.apiClient.post('/api/knowledge-base/assignments/check-conflicts', {
        operations,
        context: config.context
      });

      return response.conflicts || [];
    } catch (error) {
      console.error('Failed to check conflicts:', error);
      return [];
    }
  }, [config.context, moduleContext.apiClient]);

  // Resolve assignment conflicts
  const resolveConflicts = useCallback(async (conflicts, resolutions) => {
    try {
      const response = await moduleContext.apiClient.post('/api/knowledge-base/assignments/resolve-conflicts', {
        conflicts,
        resolutions,
        context: config.context
      });

      return response.results || [];
    } catch (error) {
      console.error('Failed to resolve conflicts:', error);
      throw error;
    }
  }, [config.context, moduleContext.apiClient]);

  // Get assignment history
  const getAssignmentHistory = useCallback(async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams({
        context: config.context,
        ...filters
      });

      const response = await moduleContext.apiClient.get(`/api/knowledge-base/assignments/history?${queryParams}`);
      return response.history || [];
    } catch (error) {
      console.error('Failed to get assignment history:', error);
      return [];
    }
  }, [config.context, moduleContext.apiClient]);

  // Cancel batch operation
  const cancelBatchOperation = useCallback(async (batchId) => {
    const batchOp = operationState.batchOperations.get(batchId);
    if (!batchOp) {
      throw new Error(`Batch operation ${batchId} not found`);
    }

    if (batchOp.status !== 'processing') {
      throw new Error(`Cannot cancel batch operation with status: ${batchOp.status}`);
    }

    setOperationState(prev => ({
      ...prev,
      batchOperations: new Map(prev.batchOperations).set(batchId, {
        ...batchOp,
        status: 'cancelled',
        cancelledAt: new Date()
      })
    }));

    return true;
  }, [operationState.batchOperations]);

  // Get operation status
  const getOperationStatus = useCallback((operationId) => {
    return operationState.activeOperations.get(operationId) || null;
  }, [operationState.activeOperations]);

  // Get batch operation status
  const getBatchOperationStatus = useCallback((batchId) => {
    return operationState.batchOperations.get(batchId) || null;
  }, [operationState.batchOperations]);

  // Clean up completed operations
  const cleanupOperations = useCallback(() => {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    setOperationState(prev => {
      const activeOperations = new Map();
      const batchOperations = new Map();

      // Keep only recent operations
      prev.activeOperations.forEach((op, id) => {
        if (op.status === 'processing' || (now - op.timestamp) < maxAge) {
          activeOperations.set(id, op);
        }
      });

      prev.batchOperations.forEach((op, id) => {
        if (op.status === 'processing' || (now - (op.completedAt || op.createdAt).getTime()) < maxAge) {
          batchOperations.set(id, op);
        }
      });

      return { activeOperations, batchOperations };
    });
  }, []);

  // Get all active operations
  const getActiveOperations = useCallback(() => {
    return {
      single: Array.from(operationState.activeOperations.values()),
      batch: Array.from(operationState.batchOperations.values())
    };
  }, [operationState]);

  return {
    toggleAssignment,
    createBatchOperation,
    executeBatchOperation,
    checkConflicts,
    resolveConflicts,
    getAssignmentHistory,
    cancelBatchOperation,
    getOperationStatus,
    getBatchOperationStatus,
    cleanupOperations,
    getActiveOperations
  };
}

export default useAssignmentManager;
